import React, { useCallback, useMemo } from 'react';
import { Grid, Label } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import FieldHeader from 'components/ideas/FieldHeader';
import { BodyContainer } from './styles';

import ShortText from './ShortText';
import LongText from './LongText';
import RichText from './RichText';
import Dropdown from './Dropdown';
import Slider from './Slider';
import OptionMultiple from './OptionMultiple';
import Image from './Image';
import Video from './Video';
import File from './File';
import MultiImage from './MultiImage';
import InfoText from './InfoText';
import CompanyReport from './CompanyReport';
import { TextFields } from '..';

export const StyledFreeformField = styled.div`
  .entry{
    &:hover{
      .settings{
        display:block;
      }
    }
    .settings{
      position:absolute;
      text-align:left;
      top:-40px;
      padding:5px;
      background:white;
      box-shadow:0px 0px 5px rgba(0,0,0,0.1);
      min-width:100px;
      display:none;
    }
  }
`;

const FieldHeaderContainer = styled.div`
  margin-bottom: 5px; 
  min-height: 40px;
`;

const CharacterLimitContainer = styled.div`
  margin-top: 5px;
  p {
    margin: 0;
    font-size: 10px;
    font-weight: ${({ exceedingCharacterLimit }) => (exceedingCharacterLimit ? 700 : 400)};
    color: ${({ exceedingCharacterLimit }) => (exceedingCharacterLimit ? 'red' : 'black')};
  }
`;

const errorMessages = {
  required: 'Please enter a value',
  characterLimit: 'You are currently exceeding the character limit',
  wordLimit: 'You are currently exceeding the word limit',
};

const requiredMessages = {
  dropdown: 'Please select a value',
  optionMultiple: 'Please select an option',
  image: 'Please select an image',
  video: 'Please upload a video',
  file: 'Please upload a file',
  multiimage: 'Please upload an image',
  slider: 'Please enter a value',
  creditsafereport: 'Please select a value',
};

export const getFieldValue = (idea, field) => idea?.templated && idea.templated[field.id];

const IdeaCustomField = withTranslation()((props) => {
  const {
    user,
    idea,
    field,
    isEditing,
    updateIdea,
    externalChanges,
    emptyRequiredField,
    incorrectFields = [],
    t,
    wordCounts,
    inputRefs,
    handleKeyDown,
    setFieldLoaded,
  } = props;

  const ideaId = idea?._id;

  /*
    The API returns a list of incorrect fields (i.e. over the word limit, over character limit, empty when required). We check whether this current field is in the incorrect fields
  */
  const incorrectFieldInfo = useMemo(() => incorrectFields.find((i) => i.id === field.id), [incorrectFields, field]);

  /*
    When the user presses submit, if there's an incorrect field, we highlight that field and scroll to it
  */
  const highlightFieldRequired = useMemo(() => field.required && emptyRequiredField && incorrectFieldInfo, [field, emptyRequiredField, incorrectFieldInfo]);

  /*
    Get a plain english error message from the incorrect field info
  */
  const errorMessage = useMemo(() => {
    if (!incorrectFieldInfo) return null;
    if (incorrectFieldInfo.type === 'required') {
      return requiredMessages[field.type] || errorMessages.required;
    } return errorMessages[incorrectFieldInfo.type];
  }, [incorrectFieldInfo, field]);

  /*
    Find an external change from collaboration users
  */
  const externalChange = useMemo(() => (externalChanges?.templated || {})[field.id], [externalChanges, field.id]);

  const fieldValue = useMemo(() => getFieldValue(idea, field), [idea, field]);

  /*
    Slider character limits are calculated on a nested field, and richtext needs the tags removed, so we have a special function for character limits

    Character limits are legacy, and will be retired at some point in favour of word limits
  */
  const characterCount = useMemo(() => {
    if (!field.characterLimit) return null;
    if (field.type === 'slider') return parseInt(field.characterLimit, 10) - parseInt(fieldValue?.notes || ''.length, 10);
    if (field.type === 'richtext') return fieldValue ? parseInt(field.characterLimit, 10) - fieldValue.replace(/(<([^>]+)>|&amp|&lt|&gt|\s)/gi, '').length : parseInt(field.characterLimit, 10);
    return parseInt(field.characterLimit, 10) - parseInt((fieldValue || '').length, 10);
  }, [field.type, field.characterLimit, fieldValue]);

  /*
    Word counts are calculated server side as they use a more complicated approach to remove whitespace, punctuation etc. These are returned to the client on each save
  */
  const wordCount = useMemo(() => {
    const wordCountItem = wordCounts?.find((w) => w.field === field.id);
    const updatedWordCount = wordCountItem?.count || 0;
    return updatedWordCount;
  }, [field.id, wordCounts]);

  const getCustomFieldComponent = useCallback((fieldType) => {
    switch (fieldType) {
      case 'shorttext': return ShortText;
      case 'longtext': return LongText;
      case 'richtext': return RichText;
      case 'dropdown': return Dropdown;
      case 'slider': return Slider;
      case 'optionMultiple': return OptionMultiple;
      case 'image': return Image;
      case 'video': return Video;
      case 'file': return File;
      case 'multiimage': return MultiImage;
      case 'creditsafereport': return CompanyReport;
      default: return null;
    }
  }, []);

  /*
    Info text and Row components required no additional styling - we display these directly
    All other fields, we get the relevant component, include all the common props, then render them alongside any character limits etc in the render body
  */
  const CustomFieldComponent = useMemo(() => {
    if (field.type === 'infotext') return isEditing ? <InfoText key={field.id} field={field} /> : null;
    if (field.type === 'row') return <BodyContainer key={field.id} computer={16} />;

    const SwitchComponent = getCustomFieldComponent(field.type);


    const extraProps = TextFields.includes(field.type) ? {
      inputRef: inputRefs.current[field.id],
      handleKeyDown: (event) => handleKeyDown(event, field.id),
    } : {};

    if (!SwitchComponent) return <React.Fragment key={field.id} />;
    return (
      <SwitchComponent
        key={field.id}
        field={field}
        ideaId={ideaId}
        user={user}
        fieldValue={fieldValue}
        errorMessage={errorMessage}
        highlightFieldRequired={highlightFieldRequired}
        isEditing={isEditing}
        updateIdea={updateIdea}
        setFieldLoaded={setFieldLoaded}
        {...extraProps}
      />
    );
  }, [field, isEditing, getCustomFieldComponent, inputRefs, handleKeyDown, ideaId, user, fieldValue, errorMessage, highlightFieldRequired, updateIdea, setFieldLoaded]);

  if ((field.type === 'infotext') || (field.type === 'row')) return CustomFieldComponent;
  return (
    <BodyContainer
      key={field.id}
      data-testid={`idea-field-${field.id}`}
      className={`field-${field.type}`}
      computer={field.width || 16}
      style={{
        boxShadow: highlightFieldRequired ? '0.5px 0.5px 5px rgb(233 11 11 / 94%)' : undefined,
        borderRadius: 5,
      }}
    >
      <FieldHeaderContainer>
        <FieldHeader
          required={field.required}
          externalChange={externalChange}
          isEditing={isEditing}
          title={field.title}
          description={field.description}
        />
      </FieldHeaderContainer>
      {CustomFieldComponent}
      {(field.wordLimit && isEditing)
        ? (
          <CharacterLimitContainer
            exceedingCharacterLimit={(field.wordLimit - wordCount) < 0}
          >
            <p>
              {t('ideas.actions.wordsRemaining', { count: (field.wordLimit - wordCount) })}
            </p>
          </CharacterLimitContainer>
        ) : null
      }
      {(field.characterLimit && field.characterLimit > 0)
        ? (
          <CharacterLimitContainer
            exceedingCharacterLimit={characterCount < 0}
          >
            <p>
              {t('ideas.actions.charactersRemaining', { count: characterCount })}
            </p>
          </CharacterLimitContainer>
        ) : null
      }
      {isEditing && highlightFieldRequired
        ? (
          <Label size='small' pointing color='red' basic >
            {errorMessage}
          </Label>
        ) : null
      }
    </BodyContainer>
  );
});

const IdeaCustomSection = (props) => {
  const {
    section,
  } = props;

  return (
    <Grid stackable centered data-testid={`idea-section-${section.id}`}>
      {section.fields?.map((field, idx) => (
        <IdeaCustomField
          key={idx}
          field={field}
          {...props}
        />
      ))}
    </Grid>
  );
};

export default withTranslation()(IdeaCustomSection);
